import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Upload, Loader2, Trash2, Archive, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { absUrl } from "@/lib/timeline";

export default function AdminBackgroundLibrary() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", category: "", tags: "" });
  const fileRef = useRef(null);

  const load = () => api.adminListBackgrounds().then((d) => setItems(d.backgrounds || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("name", form.name || f.name);
      fd.append("category", form.category);
      fd.append("tags", form.tags);
      await api.adminCreateBackground(fd);
      toast.success("Background uploaded");
      setForm({ name: "", category: "", tags: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
    setBusy(false);
  };

  const toggleActive = async (b) => { await api.adminUpdateBackground(b.id, { active: !b.active }); load(); };
  const archive = async (b) => { await api.adminDeleteBackground(b.id); toast.success("Archived"); load(); };
  const hardDelete = async (b) => { if (!window.confirm(`Permanently delete "${b.name}"? Projects using it will lose the asset.`)) return; await api.adminDeleteBackground(b.id, true); toast.success("Deleted"); load(); };
  const editField = async (b, key, val) => { await api.adminUpdateBackground(b.id, { [key]: val }); load(); };

  return (
    <div data-testid="admin-background-library">
      <h2 className="mb-1 font-display text-2xl font-black text-white">Background Library</h2>
      <p className="mb-5 text-sm text-[#A1A1AA]">Upload reusable stadium / news / cinematic backgrounds (images or looping videos). Only ACTIVE items appear to users.</p>

      <div className="mb-6 rounded-2xl border border-white/10 bg-white/[0.02] p-4" data-testid="bg-upload-form">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <input data-testid="bg-upload-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-[#00F0FF]/50" />
          <input data-testid="bg-upload-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Category (e.g. Cricket)" className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-[#00F0FF]/50" />
          <input data-testid="bg-upload-tags" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="Tags (comma separated)" className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-[#00F0FF]/50" />
        </div>
        <input ref={fileRef} type="file" accept="image/*,video/mp4,video/quicktime,video/webm" className="hidden" onChange={onFile} data-testid="bg-upload-input" />
        <button data-testid="bg-upload-btn" disabled={busy} onClick={() => fileRef.current?.click()}
          className="mt-3 flex items-center gap-2 rounded-lg bg-[#00F0FF] px-4 py-2 text-sm font-bold text-black hover:bg-[#00d4e0] disabled:opacity-60">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload image / video
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="bg-admin-grid">
        {items.length === 0 && <p className="col-span-full text-sm text-[#888]">No backgrounds yet.</p>}
        {items.map((b) => (
          <div key={b.id} data-testid={`bg-admin-item-${b.id}`} className={`overflow-hidden rounded-xl border ${b.active ? "border-white/10" : "border-white/5 opacity-60"} bg-white/[0.02]`}>
            <div className="relative aspect-video bg-black">
              <img src={absUrl(b.thumbnail_url || b.storage_url)} alt={b.name} className="h-full w-full object-cover" />
              {b.media_type === "video" && <span className="absolute bottom-1 right-1 rounded bg-black/70 px-1 text-[9px] font-bold text-white">VIDEO {b.duration ? `${Math.round(b.duration)}s` : ""}</span>}
              {!b.active && <span className="absolute left-1 top-1 rounded bg-[#f59e0b]/90 px-1.5 text-[9px] font-bold text-black">ARCHIVED</span>}
            </div>
            <div className="p-2">
              <input value={b.name} onChange={(e) => setItems((prev) => prev.map((x) => x.id === b.id ? { ...x, name: e.target.value } : x))} onBlur={(e) => editField(b, "name", e.target.value)}
                className="mb-1 w-full bg-transparent text-xs font-semibold text-white outline-none" />
              <input value={b.category || ""} placeholder="category" onChange={(e) => setItems((prev) => prev.map((x) => x.id === b.id ? { ...x, category: e.target.value } : x))} onBlur={(e) => editField(b, "category", e.target.value)}
                className="mb-2 w-full bg-transparent text-[10px] text-[#A1A1AA] outline-none" />
              <div className="flex items-center gap-1">
                <button data-testid={`bg-toggle-${b.id}`} onClick={() => toggleActive(b)} title={b.active ? "Deactivate" : "Activate"} className="flex-1 rounded border border-white/15 py-1 text-[10px] font-semibold text-[#A1A1AA] hover:text-white">{b.active ? <span className="flex items-center justify-center gap-1"><CheckCircle2 size={11} className="text-[#22c55e]" /> Active</span> : "Inactive"}</button>
                <button data-testid={`bg-archive-${b.id}`} onClick={() => archive(b)} title="Archive" className="rounded border border-white/15 p-1 text-[#A1A1AA] hover:text-[#f59e0b]"><Archive size={12} /></button>
                <button data-testid={`bg-delete-${b.id}`} onClick={() => hardDelete(b)} title="Delete permanently" className="rounded border border-white/15 p-1 text-[#A1A1AA] hover:text-red-400"><Trash2 size={12} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
